import React, { useEffect, useImperativeHandle } from "react";
import * as yup from 'yup';
import { useFormik } from 'formik';
import { IDataFromModal } from "../pages/Workspace/interface";
import { insertPayment } from "../Services/API";
import { AlertColor, Box, Grid } from '@mui/material';
import { useFormContext } from "react-hook-form";
import { PrimaryButton, SecondaryButton } from "../components/Custom/Buttons";
import { useScrollToElement } from "../CustomHooks/useScrollToElement";
import { errorFields, getErrorStatus, traceFormikErrorField } from "../pages/CreateForm/formikutils";
import CreateFormPopupContent from "../pages/CreateForm";
import CustomizedInput from "../components/CreateForm/InputBase";
import './style.scss'
import appContext from "../appContext";

type popupprop = {
    landingPage: boolean,
    setLandingPage: (landingPage: boolean) => void,
    dataFromModal: IDataFromModal,
    setDataFromModal: (dataFromModal: any) => void,
    handlingModalData: () => void,
    endPointAvailable: boolean,
    setEndPointAvailable: (endPointAvailable: boolean) => void,
    recordVideo: boolean,
    setRecordVideo: (recordVideo: boolean) => void,
    editUUID: string,
    showRecordVideo: boolean,
    setShowRecordVideo: (showRecordVideo: boolean) => void,
    sendResponseToUser: boolean,
    setSendResponseToUser: (sendResponseToUser: boolean) => void,
    gaAvailable: boolean,
    setGaAvailable: (gaAvailable: boolean) => void,
    gtmAvailable: boolean,
    setGtmAvailable: (gaAvailable: boolean) => void,
    gaTrackingId: string,
    gtmTrackingId: string,
    applicationPopupData: string,
    setApplicationPopupData: (e: string) => void,
    departmentPopupData: string,
    setDepartmentPopupData: (e: string) => void,
    setWatermark: (e: boolean) => void,
    watermark: boolean,
    watermarkPosition: string,
    setWatermarkPosition: (e: string) => void,
    casePopupData: string,
    setCasePopupData: (e: string) => void,
    popoverSubmit?: any,
    saveContact: boolean,
    setSaveContact: (saveContact: boolean) => void,
    setContactGroup: (contactGroup: string) => void,
    contactGroup: string
}

type paymentProps = {
    paymentUUID?: string,
    setPaymentDetails: (e: any) => void,
    selectedData: any,
    handleDelete: (e: any) => void,
    setErrorMessage: (e: string) => void,
    setErrorType: (e: AlertColor) => void,
    setToastr: (e: boolean) => void,
    handleCancel: () => void,
}

const PaymentItemContainer = ({ itemKey, title, validatorFormik }: any) => {
    return (
        <Grid container margin="10px">
            <Grid item xs={3} display="flex" alignItems="center">
                {title}
                <span className="err-start">*</span>
            </Grid>
            <Grid item xs={9}>
                <CustomizedInput
                    placeholder="Type here"
                    {...validatorFormik?.getFieldProps(itemKey)}
                />
                {validatorFormik?.touched?.[itemKey] &&
                    validatorFormik?.errors?.[itemKey] ? (
                    <div className="warn" style={{ marginTop: 5 }}>
                        {validatorFormik?.errors?.[itemKey]}
                    </div>
                ) : null}
            </Grid>
        </Grid>
    );
};

const PopupContent: React.FC<popupprop> = ({ landingPage, setLandingPage, dataFromModal, setDataFromModal, handlingModalData, endPointAvailable, setEndPointAvailable, editUUID, recordVideo, setRecordVideo, showRecordVideo, setShowRecordVideo, sendResponseToUser, setSendResponseToUser, gaAvailable, setGaAvailable, gtmAvailable, setGtmAvailable, gaTrackingId, gtmTrackingId, applicationPopupData, setApplicationPopupData, departmentPopupData, setDepartmentPopupData, watermark, setWatermark, setWatermarkPosition, watermarkPosition, casePopupData, setCasePopupData, popoverSubmit, saveContact, setSaveContact, contactGroup, setContactGroup }) => {
    const { scrollToElement } = useScrollToElement();
    const { setCurrentFormtitle } = React.useContext(appContext);
    const initialLandingPageSchema = {
        name: '',
        backGround: '',
        landingPageBackground: '',
        submitPageBackground: '',
        isLanding: landingPage,
        html: "",
        finalContent: "",
        landTextColor: "#ffffff",
        landBtnColor: "#7654ff",
        backgroundColor: "#3d3d3d",
        landContainerContentColour: "#ffffff",
        textColor: "#000000",
        btnColor: "#ffffff",
        btnTextColor: "#000000",
        loaderColor: "#ffffff",
        endPointAvailable: dataFromModal?.endPointAvailable ?? endPointAvailable,
        recordVideo: dataFromModal?.recordVideo ?? recordVideo,
        showRecordVideo: dataFromModal?.showRecordVideo ?? showRecordVideo,
        sendResponseToUser: dataFromModal?.sendResponseToUser ?? sendResponseToUser,
        allowNewResponse: dataFromModal?.allowNewResponse,
        saveContact: dataFromModal?.saveContact ?? saveContact,
        contactGroup: dataFromModal?.contactGroup ?? contactGroup,
        gaAvailable: dataFromModal?.gaAvailable ?? gaAvailable,
        gtmAvailable: dataFromModal?.gtmAvailable ?? gtmAvailable,
        endPoint: "",
        endPointMethod: "",
        endPointHeaders: "",
        gaTrackingId: "",
        gtmTrackingId: "",
        casePopupData: casePopupData,
        sendResponseInEmail: {
            isActive: dataFromModal.sendResponseInEmail?.isActive ?? false,
            sendersEmail: dataFromModal.sendResponseInEmail?.sendersEmail ?? "",
            recipientsEmail: dataFromModal.sendResponseInEmail?.recipientsEmail ?? [],
            subject: dataFromModal.sendResponseInEmail?.subject ?? ""
        },
        isPublished: 0,
        templateId: dataFromModal?.templateId || ""
    }
    const landingPageFormValidator = yup.object().shape({
        name: yup.string().min(1, '*name must not be empty').required('*name must not be empty'),
        backGround: yup.string().url("Enter valid url").min(1, '*backGround must not be empty').optional(),
        landingPageBackground: yup.string().url("Enter valid url").optional(),
        submitPageBackground: yup.string().url("Enter valid url").optional(),
        finalContent: yup.string(),
        html: yup.string().when('isLanding', {
            is: true,
            then: (schema) => schema.required('Landing page HTML/Content is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        landTextColor: yup.string().when('isLanding', {
            is: true,
            then: (schema) => schema.required('field is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        landBtnColor: yup.string().when('isLanding', {
            is: true,
            then: (schema) => schema.required('field is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        textColor: yup.string().min(1, '*textColor must not be empty').required('*textColor must not be empty'),
        btnColor: yup.string().min(1, '*btnColor must not be empty').required('*btnColor must not be empty'),
        btnTextColor: yup.string().min(1, '*btnTextColor must not be empty').required('*btnTextColor must not be empty'),
        loaderColor: yup.string().min(1, '*loaderColor must not be empty').required('*loaderColor must not be empty'),
        endPoint: yup.string().when('endPointAvailable', {
            is: true,
            then: (schema) => schema.required('EndPoint is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        gaTrackingId: yup.string().when('gaAvailable', {
            is: true,
            then: (schema) => schema.required('GA Measurement Id is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        gtmTrackingId: yup.string().when('gtmAvailable', {
            is: true,
            then: (schema) => schema.required('GTM tag is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        endPointHeaders: yup.string().test('endPointHeaders', 'Invalid JSON', value => {
            if (!value) {
                return true;
            }
            try {
                JSON.parse(value);
                return true;
            } catch (e) {
                return false;
            }
        }),
        contactGroup: yup.string().when('saveContact', {
            is: true,
            then: (schema) => schema.required('Please select group to store your contact'),
            otherwise: (schema) => schema.notRequired(),

        }),
        casePopupData: yup.string().required('*Use case must not be empty'),
        sendResponseInEmail: yup.object().shape({
            sendersEmail: yup.string().when('isActive', {
                is: true,
                then: (schema) => schema.email('Please enter a valid email').required('Please enter senders email'),
                otherwise: (schema) => schema.notRequired(),
            }),
            recipientsEmail: yup.array().of(yup.string().email('Please enter a valid email')).when('isActive', {
                is: true,
                then: (schema) => schema.min(1, 'Please enter recipients email'),
                otherwise: (schema) => schema.notRequired(),
            }),
            subject: yup.string().when('isActive', {
                is: true,
                then: (schema) => schema.required('Please enter subject for email'),
                otherwise: (schema) => schema.notRequired()
            })
        })
    });

    const handleSubmit = async (values: any) => {
        setDataFromModal({ ...values });
        setCasePopupData(values.casePopupData);
        handlingModalData();
    };

    var validatorFormik: any = useFormik({ initialValues: initialLandingPageSchema, validationSchema: landingPageFormValidator, onSubmit: handleSubmit });

    useEffect(() => {
        validatorFormik.setFieldValue("name", dataFromModal?.name);
        validatorFormik.setFieldValue("backGround", dataFromModal?.backGround);
        validatorFormik.setFieldValue("landingPageBackground", dataFromModal?.landingPageBackground);
        validatorFormik.setFieldValue("submitPageBackground", dataFromModal?.submitPageBackground);
        validatorFormik.setFieldValue("finalContent", dataFromModal?.finalContent)
        validatorFormik.setFieldValue("html", dataFromModal?.html);
        validatorFormik.setFieldValue("landTextColor", dataFromModal?.landTextColor);
        validatorFormik.setFieldValue("landBtnColor", dataFromModal?.landBtnColor);
        validatorFormik.setFieldValue("landBtnText", dataFromModal?.landBtnText);
        validatorFormik.setFieldValue("landBtnFont", dataFromModal?.landBtnFont);
        validatorFormik.setFieldValue("landContentType", dataFromModal?.landContentType);
        validatorFormik.setFieldValue("landContainerContentColour", dataFromModal?.landContainerContentColour);
        validatorFormik.setFieldValue("landCustomContentColour", dataFromModal?.landCustomContentColour);
        validatorFormik.setFieldValue("landCustomContentFont", dataFromModal?.landCustomContentFont);
        validatorFormik.setFieldValue("textColor", dataFromModal?.textColor);
        validatorFormik.setFieldValue("backgroundColor", dataFromModal?.backgroundColor);
        validatorFormik.setFieldValue("btnColor", dataFromModal?.btnColor);
        validatorFormik.setFieldValue("btnTextColor", dataFromModal?.btnTextColor);
        validatorFormik.setFieldValue("loaderColor", dataFromModal?.loaderColor);
        setLandingPage(dataFromModal?.isLanding ?? landingPage);
        validatorFormik.setFieldValue("isLanding", dataFromModal?.isLanding ?? landingPage);
        setEndPointAvailable(dataFromModal?.endPointAvailable ?? endPointAvailable)
        setSendResponseToUser(dataFromModal?.sendResponseToUser ?? sendResponseToUser)
        setSaveContact(dataFromModal?.saveContact ?? saveContact)
        setContactGroup(dataFromModal?.contactGroup ?? contactGroup)
        setGaAvailable(dataFromModal?.gaAvailable ?? gaAvailable)
        setGtmAvailable(dataFromModal?.gtmAvailable ?? gtmAvailable)
        validatorFormik.setFieldValue("endPoint", dataFromModal?.endPoint);
        validatorFormik.setFieldValue("endPointMethod", dataFromModal?.endPointMethod);
        validatorFormik.setFieldValue("endPointHeaders", dataFromModal?.endPointHeaders);
        validatorFormik.setFieldValue("recordVideo", dataFromModal?.recordVideo);
        validatorFormik.setFieldValue("showRecordVideo", dataFromModal?.showRecordVideo);
        validatorFormik.setFieldValue("saveContact", dataFromModal?.saveContact);
        validatorFormik.setFieldValue("contactGroup", dataFromModal?.contactGroup);
        validatorFormik.setFieldValue("sendResponseToUser", dataFromModal?.sendResponseToUser);
        validatorFormik.setFieldValue("gaAvailable", dataFromModal?.gaAvailable);
        validatorFormik.setFieldValue("gtmAvailable", dataFromModal?.gtmAvailable);
        validatorFormik.setFieldValue("gaTrackingId", dataFromModal?.gaTrackingId);
        validatorFormik.setFieldValue("gtmTrackingId", dataFromModal?.gtmTrackingId);
        validatorFormik.setFieldValue("isPublished", dataFromModal?.isPublished);
        validatorFormik.setFieldValue("allowNewResponse", dataFromModal?.allowNewResponse);
        validatorFormik.setFieldValue("sendResponseInEmail.isActive", dataFromModal?.sendResponseInEmail?.isActive);
        validatorFormik.setFieldValue("sendResponseInEmail.sendersEmail", dataFromModal?.sendResponseInEmail?.sendersEmail);
        validatorFormik.setFieldValue("sendResponseInEmail.recipientsEmail", dataFromModal?.sendResponseInEmail?.recipientsEmail);
        validatorFormik.setFieldValue("sendResponseInEmail.subject", dataFromModal?.sendResponseInEmail?.subject);
        setCurrentFormtitle(dataFromModal?.name || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataFromModal])

    useEffect(() => {
        validatorFormik.setFieldValue("casePopupData", casePopupData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [casePopupData])

    const submitHandler = () => {
        validatorFormik?.handleSubmit();
    };

    const getValidationStatus = () => {
        return getErrorStatus(validatorFormik);
    };

    const scrollToErrorField = () => {
        const errorHandler = (field: string) => {
            scrollToElement(field);
        };

        errorFields.forEach((field) => {
            validatorFormik.setFieldTouched(field, true);
        });

        traceFormikErrorField(errorHandler, validatorFormik);
    };

    const getTitle = () => {
        const field = validatorFormik?.getFieldProps("name");

        return field ? field.value : "";
    };

    useImperativeHandle(popoverSubmit, () => ({
        submitHandler,
        scrollToErrorField,
        getValidationStatus,
        getTitle,
    }));

    return (
        <div id="create-form-content-parent">
            <form>
                <CreateFormPopupContent
                    values={validatorFormik}
                    dataFromModal={dataFromModal}
                    setDataFromModal={setDataFromModal}
                    landingPage={landingPage}
                    setLandingPage={setLandingPage}
                    applicationPopupData={applicationPopupData}
                    setApplicationPopupData={setApplicationPopupData}
                    departmentPopupData={departmentPopupData}
                    setDepartmentPopupData={setDepartmentPopupData}
                    watermarkPosition={watermarkPosition}
                    setWatermarkPosition={setWatermarkPosition}
                    watermark={watermark}
                    setWatermark={setWatermark}
                    recordVideo={recordVideo}
                    setRecordVideo={setRecordVideo}
                    showRecordVideo={showRecordVideo}
                    setShowRecordVideo={setShowRecordVideo}
                    sendResponseToUser={sendResponseToUser}
                    setSendResponseToUser={setSendResponseToUser}
                    endPointAvailable={endPointAvailable}
                    setEndPointAvailable={setEndPointAvailable}
                    gaAvailable={gaAvailable}
                    setGaAvailable={setGaAvailable}
                    gtmAvailable={gtmAvailable}
                    setGtmAvailable={setGtmAvailable}
                    editUUID={editUUID}
                    saveContact={saveContact}
                    setSaveContact={setSaveContact}
                />
            </form>
        </div>
    );
}

const PaymentPopUp: React.FC<paymentProps> = ({ setToastr, paymentUUID, setPaymentDetails, selectedData, handleDelete, setErrorType, setErrorMessage, handleCancel }) => {
    const { setValue } = useFormContext();

    const initialSchema = {
        merchantId: "",
        publicKey: "",
        privateKey: ""
    }

    const formValidator = yup.object().shape({
        merchantId: yup.string().required('*merchantId must not be empty'),
        publicKey: yup.string().required('*publicKey must not be empty'),
        privateKey: yup.string().required('*privateKey must not be empty'),
    });

    const savePaymentDetails = async (data: any) => {
        const payload: any = {
            merchantId: data?.merchantId,
            publicKey: data?.publicKey,
            privateKey: data?.privateKey,
            principalId: sessionStorage.principalId
        }
        if (paymentUUID) {
            payload["paymentUUID"] = paymentUUID;
        }
        const paymentDetails = await insertPayment(payload);

        if (paymentDetails.statusCode === 200) {
            setValue("paymentId", paymentDetails?.data?.paymentUUID)
            setPaymentDetails(false);
            setToastr(true);
        }
        else {
            handleDelete(selectedData.index)
            setToastr(true);
            setErrorMessage("Something went wrong please try again later")
            setErrorType('error')
            setTimeout(() => {
                setErrorMessage("updated successfully")
                setErrorType('success')
                setPaymentDetails(false);
            }, 3000)
        }
    }

    const validatorFormik = useFormik({ initialValues: initialSchema, validationSchema: formValidator, onSubmit: savePaymentDetails });

    return (
        <form onSubmit={validatorFormik?.handleSubmit} className="payment-content-wrap">
            <PaymentItemContainer
                itemKey={"merchantId"}
                title={"Merchant ID"}
                validatorFormik={validatorFormik}
            />
            <PaymentItemContainer
                itemKey={"publicKey"}
                title={"Public Key"}
                validatorFormik={validatorFormik}
            />
            <PaymentItemContainer
                itemKey={"privateKey"}
                title={"Private Key"}
                validatorFormik={validatorFormik}
            />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-evenly", width: "100%", height: "40px", marginTop: "15px" }}>
                <SecondaryButton style={{ width: "40%" }} onClick={handleCancel}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" style={{ width: "40%" }}>
                    Save
                </PrimaryButton>
            </Box>
        </form>
    )
}

export { PopupContent, PaymentPopUp }